import { useEffect, useMemo, useState } from "react"
import { faClose, faTags, faUtensils } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { useConfirmation } from "../../../context/useConfirmationContext"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { Permissions } from "../../../Enum/PermissionsType"
import { drinksMemory, mealsMemory } from "../../../Objects/Memory/Memory"
import type { RodizioPlan } from "../../../Objects/Rodizio/RodizioPlan"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import {
  ProductCompositionSelector,
  type ProductCompositionItem,
} from "../../../components/ProductCompositionSelector/ProductCompositionSelector"
import GetSpace from "../../../utils/space"

interface Props {
  plan?: RodizioPlan
  isLoading: boolean
  onClose: () => void
  onCopy?: () => void
  onSave: (plan: RodizioPlan) => Promise<boolean>
  onToggleStatus: (plan: RodizioPlan) => Promise<boolean>
  onRemove: (plan: RodizioPlan) => Promise<boolean>
}

const emptyPlan = (): RodizioPlan => ({
  id: 0,
  name: "",
  description: "",
  isActive: true,
  chargeMealEntityId: mealsMemory.listData[0]?.id ?? 0,
  chargeProductName: "",
  price: 0,
  includeAdditionals: false,
  productIds: [],
})

export const RodizioPlanPopup: React.FC<Props> = ({
  plan,
  isLoading,
  onClose,
  onCopy,
  onSave,
  onToggleStatus,
  onRemove,
}) => {
  const { t } = useTranslation("rodizio")

  const isExistingPlan = !!plan && plan.id > 0

  const isCopiedDraft = plan?.id === 0

  const { setNewConfirmation } = useConfirmation()

  const [draft, setDraft] = useState<RodizioPlan>(() =>
    plan ? { ...plan, productIds: [...plan.productIds] } : emptyPlan(),
  )

  useEffect(() => {
    setDraft(plan ? { ...plan, productIds: [...plan.productIds] } : emptyPlan())
  }, [plan])

  const products = useMemo(
    () => [
      ...mealsMemory.listData.map((item) => ({
        id: item.genericFoodEntity.id,
        name: item.genericFoodEntity.name,
        iconUrl: item.genericFoodEntity.iconUrl,
        group: t("plan.meals"),
        groupKey: "meal",
      })),
      ...drinksMemory.listData.map((item) => ({
        id: item.genericFoodEntity.id,
        name: item.genericFoodEntity.name,
        iconUrl: item.genericFoodEntity.iconUrl,
        group: t("plan.drinks"),
        groupKey: "drink",
      })),
    ],
    [t],
  )

  const chargeProductGenericFoodId = useMemo(
    () => mealsMemory.listData.find((item) => item.id === draft.chargeMealEntityId)?.genericFoodEntity.id,
    [draft.chargeMealEntityId],
  )

  useEffect(() => {
    if (!chargeProductGenericFoodId) return
    setDraft((current) =>
      current.productIds.includes(chargeProductGenericFoodId)
        ? { ...current, productIds: current.productIds.filter((id) => id !== chargeProductGenericFoodId) }
        : current,
    )
  }, [chargeProductGenericFoodId])

  const selectedProducts = useMemo<ProductCompositionItem[]>(
    () =>
      products
        .filter((product) => draft.productIds.includes(product.id))
        .map((product) => ({
          key: String(product.id),
          name: product.name,
          typeLabel: product.group,
          iconUrl: product.iconUrl ? product.iconUrl.toClientRequest() : undefined,
          fallbackIcon: product.groupKey === "meal" ? "🍽️" : "🥤",
        })),
    [draft.productIds, products],
  )

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={isExistingPlan ? t("plan.edit") : t("plan.create")}
              icon={faUtensils}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex-nowrap`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="rodizio.plan"
                    title={t("helpPopup:global.rodizioPlan.title")}
                    description={t("helpPopup:global.rodizioPlan.description")}
                    steps={t("helpPopup:global.rodizioPlan.steps", { returnObjects: true }) as string[]}
                    bullets={t("helpPopup:global.rodizioPlan.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:rodizio.plan.close"
                  />
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <form
                className="grid max-h-[75vh] gap-3 overflow-y-auto p-2"
                onSubmit={async (event) => {
                  event.preventDefault()
                  if (await onSave(draft)) onClose()
                }}
              >
                <GenericDropDown
                  legend={t("plan.charge_product")}
                  data={mealsMemory.listData.map((item) => ({
                    value: String(item.id),
                    label: `${item.genericFoodEntity.name} — ${item.genericFoodEntity.valueBase.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`,
                  }))}
                  initialState={String(draft.chargeMealEntityId)}
                  handlerFuncion={(event) =>
                    setDraft((current) => ({ ...current, chargeMealEntityId: Number(event.target.value) }))
                  }
                  descriptionKey="interactionDescriptions:rodizio.plan.chargeProduct"
                />

                <div className={defaultStyle.divisionBar}></div>

                <InputField
                  type="text"
                  icon={faTags}
                  value={draft.name}
                  maxLength={120}
                  required
                  placeholder={t("plan.name")}
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                  descriptionKey="interactionDescriptions:rodizio.plan.name"
                />

                <InputField
                  type="text"
                  icon={faTags}
                  value={draft.description ?? ""}
                  maxLength={500}
                  placeholder={t("plan.description")}
                  onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                  descriptionKey="interactionDescriptions:rodizio.plan.description"
                />

                <SimpleToggle
                  title={t("plan.active")}
                  value={draft.isActive}
                  onChange={(value) => setDraft((current) => ({ ...current, isActive: value }))}
                  descriptionKey="interactionDescriptions:rodizio.plan.active"
                />

                <SimpleToggle
                  title={t("plan.include_additionals")}
                  value={draft.includeAdditionals}
                  onChange={(value) => setDraft((current) => ({ ...current, includeAdditionals: value }))}
                  descriptionKey="interactionDescriptions:rodizio.plan.additionals"
                />

                <div className={defaultStyle.divisionBar}></div>

                <ProductCompositionSelector
                  title={t("plan.included_products")}
                  description={t("plan.included_products_help")}
                  groups={[
                    {
                      key: "meal",
                      label: t("plan.meals"),
                      placeholder: t("plan.select_meal"),
                      options: products
                        .filter(
                          (product) =>
                            product.groupKey === "meal" &&
                            !draft.productIds.includes(product.id) &&
                            product.id !== chargeProductGenericFoodId,
                        )
                        .map((product) => ({ id: product.id, label: product.name })),
                      selectDescriptionKey: "interactionDescriptions:rodizio.plan.selectMeal",
                      addDescriptionKey: "interactionDescriptions:rodizio.plan.addMeal",
                    },
                    {
                      key: "drink",
                      label: t("plan.drinks"),
                      placeholder: t("plan.select_drink"),
                      options: products
                        .filter((product) => product.groupKey === "drink" && !draft.productIds.includes(product.id))
                        .map((product) => ({ id: product.id, label: product.name })),
                      selectDescriptionKey: "interactionDescriptions:rodizio.plan.selectDrink",
                      addDescriptionKey: "interactionDescriptions:rodizio.plan.addDrink",
                    },
                  ]}
                  items={selectedProducts}
                  emptyLabel={t("plan.no_products_selected")}
                  removeLabel={t("plan.remove_product")}
                  quantityLabel={t("plan.quantity")}
                  onAdd={(_, productId) =>
                    setDraft((current) => ({
                      ...current,
                      productIds: current.productIds.includes(productId)
                        ? current.productIds
                        : [...current.productIds, productId],
                    }))
                  }
                  onRemove={(productId) =>
                    setDraft((current) => ({
                      ...current,
                      productIds: current.productIds.filter((id) => id !== Number(productId)),
                    }))
                  }
                />

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    label={isLoading ? t("common.saving") : t("common.save")}
                    typeButton="submit"
                    disabled={
                      isLoading || !draft.name.trim() || draft.chargeMealEntityId <= 0 || draft.productIds.length === 0
                    }
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:rodizio.plan.save"
                  />

                  {isExistingPlan && onCopy && (
                    <CanView permission={Permissions.CanEditeCompany}>
                      <Button
                        label={t("buttons:button_copy")}
                        typeButton="button"
                        disabled={isLoading}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        onClick={onCopy}
                        descriptionKey="interactionDescriptions:rodizio.plan.copy"
                      />
                    </CanView>
                  )}

                  {isExistingPlan && plan && (
                    <CanView permission={Permissions.CanEditeCompany}>
                      <>
                        <Button
                          label={plan.isActive ? t("plan.deactivate") : t("plan.activate")}
                          typeButton="button"
                          disabled={isLoading}
                          bgColor={THEME.colors.buttonBGRegisterColor}
                          onClick={() =>
                            setNewConfirmation(
                              plan.isActive ? t("plan.confirm_deactivate_title") : t("plan.confirm_activate_title"),
                              t(plan.isActive ? "plan.confirm_deactivate_message" : "plan.confirm_activate_message", {
                                name: plan.name,
                              }),
                              async () => {
                                if (await onToggleStatus(plan)) onClose()
                              },
                            )
                          }
                          descriptionKey="interactionDescriptions:rodizio.plan.toggleStatus"
                        />

                        <Button
                          label={t("plan.remove")}
                          typeButton="button"
                          disabled={isLoading}
                          iconColor={THEME.colors.buttonFGRemoveColor}
                          bgColor={THEME.colors.buttonBGRemoveColor}
                          onClick={() =>
                            setNewConfirmation(
                              t("plan.confirm_remove_plan_title"),
                              t("plan.confirm_remove_plan_message", { name: plan.name }),
                              async () => {
                                if (await onRemove(plan)) onClose()
                              },
                            )
                          }
                          descriptionKey="interactionDescriptions:rodizio.plan.remove"
                        />
                      </>
                    </CanView>
                  )}
                </div>
              </form>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
